import { useAllVoices } from '@/hooks/useAllVoices';
import { Page } from '@/pages/AdvancedEditor/AdvancedEditor';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import {
    CacheStatus,
    getPageProperty,
    PolotnoStoreActivePageKeys,
    PolotnoStoreKeys,
    setPageProperty,
    usePolotnoState,
} from '@/pages/AdvancedEditor/hooks';
import { polotnoTypes } from '@/pages/AdvancedEditor/types';
import { checkPolotnoType, getAvatarElementFromPage } from '@/pages/AdvancedEditor/utils';
import { getDefaultVoice } from '@/services/presenters/getDefaultVoice';
import { CropMode } from '@/utils/types';
import { getImageSize } from '@/utils/utils';
import { Presenter, ScriptType, VideoType } from '@d-id/types';
import { nanoid } from 'nanoid';
import { ElementType } from 'polotno/model/group-model';
import { ImageElementType } from 'polotno/model/image-model';
import { StoreType } from 'polotno/model/store';
import { useEffect, useState } from 'react';
import { AvatarView } from '../ModifyPresenter/controls/ViewControl/ViewControl';
import { inheritAvatarProperties } from './inheritAvatarProperties';

const getScaledImage = (width: number, height: number, maxWidth: number, maxHeight: number) => {
    const scale = Math.min(maxWidth / width, maxHeight / height);
    return {
        width: width * scale,
        height: height * scale,
    };
};

export const getAvatarRect = (originalWidth: number, originalHeight: number, store: StoreType, avatar?: ElementType) => {
    const { width, height } = getScaledImage(originalWidth, originalHeight, store.width, store.height);

    if (avatar) {
        const center = {
            x: avatar.x + avatar.width / 2,
            y: avatar.y + avatar.height / 2,
        };

        return {
            x: center.x - width / 2,
            y: center.y - height / 2,
            width,
            height,
        };
    }

    return {
        x: +store.activePage!.computedWidth / 2 - width / 2,
        y: +store.activePage!.computedHeight / 2 - height / 2,
        width,
        height,
    };
};

interface AddAvatarOptions {
    skipCrop?: boolean;
}

const DEFAULT_AVATAR_SHADOW_PROPS = {
    shadowBlur: 10,
    shadowColor: '#000000',
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    shadowOpacity: 0.4,
};

export const useAddAvatarToPolotno = () => {
    const { store, editorInitialized } = useAdvancedEditorContext();
    const [bufferedAvatar, setBufferedAvatar] = useState<{ presenter: Presenter; options?: AddAvatarOptions } | undefined>();

    const [cropSettings] = usePolotnoState(PolotnoStoreKeys.cropSettings);
    const { data: voices = [] } = useAllVoices();

    useEffect(() => {
        if (bufferedAvatar && store.activePage && editorInitialized) {
            addAvatar(store.activePage, bufferedAvatar.presenter, bufferedAvatar.options);
            setBufferedAvatar(undefined);
        }
    }, [bufferedAvatar, store.activePage, editorInitialized]);

    const addAvatar = async (
        page: Page,
        presenter: Presenter & {
            x?: number;
            y?: number;
            width?: number;
            height?: number;
        },
        options?: AddAvatarOptions
    ) => {
        setPageProperty(page, PolotnoStoreActivePageKeys.isAddingAvatar)(true);
        const avatar = getAvatarElementFromPage(page);
        if (avatar) {
            store.selectElements([avatar.id]);
        }

        if (page && editorInitialized) {
            store.history.transaction(async () => {
                const avatars = page.children.filter(child => checkPolotnoType(child, polotnoTypes.Avatar));
                const avatar = avatars[0] as ImageElementType | undefined;

                const isFitMode = cropSettings === CropMode.Fit;
                const fitModeAvatarResized = avatar && (avatar.height < +page.height || avatar.width < +page.width);
                const shouldResize = isFitMode && !fitModeAvatarResized;

                const { width: originalWidth, height: originalHeight } = await getImageSize(presenter.poster);

                const { x, y, width, height } = getAvatarRect(originalWidth, originalHeight, store, avatar);

                if (shouldResize) {
                    store.setSize(width, height, true);
                    page.set({ width, height });
                }

                const { type } = presenter;

                if (avatars?.length) {
                    store.deleteElements(avatars.map(a => a.id));
                }

                const selectedPresenterType = getPageProperty(page, PolotnoStoreActivePageKeys.selectedPresenterType);
                setPageProperty(page, PolotnoStoreActivePageKeys.selectedPresenterType)(presenter?.presenterType || selectedPresenterType);

                setPageProperty(page, PolotnoStoreActivePageKeys.selectedPresenter)(presenter.id);
                setPageProperty(page, PolotnoStoreActivePageKeys.selectedDriver)(undefined);
                setPageProperty(page, PolotnoStoreActivePageKeys.selectedEmotion)(undefined);

                const selectedScript = getPageProperty(page, PolotnoStoreActivePageKeys.selectedScript);
                if (selectedScript === ScriptType.text) {
                    setPageProperty(page, PolotnoStoreActivePageKeys.ttsAudioCacheStatus)(CacheStatus.Invalid);
                    setPageProperty(page, PolotnoStoreActivePageKeys.sceneDurationCalculated)(false);
                    setPageProperty(page, PolotnoStoreActivePageKeys.ttsCreated)(false);
                }

                if (presenter.type === VideoType.EXPRESSIVE && presenter?.sentiments?.length) {
                    setPageProperty(page, PolotnoStoreActivePageKeys.selectedSentimentId)(presenter.sentiments[0].id);
                }

                const isDefaultVoice = getPageProperty(page, PolotnoStoreActivePageKeys.ttsIsSelectedDefault);
                const defaultPresenterVoice = getDefaultVoice(presenter, voices);

                if (isDefaultVoice && defaultPresenterVoice) {
                    setPageProperty(page, PolotnoStoreActivePageKeys.ttsSelectedVoice)(defaultPresenterVoice);
                }

                const inheritedProperties = inheritAvatarProperties(presenter, originalWidth, originalHeight, avatar);

                const element = {
                    ...DEFAULT_AVATAR_SHADOW_PROPS,
                    id: nanoid(),
                    type: 'image',
                    videoType: type,
                    alwaysOnTop: false,
                    src: presenter.poster,
                    contentEditable: false,
                    keepRatio: true,
                    ...(inheritedProperties || {}),
                    custom: {
                        view: AvatarView.Full,
                        presenter,
                        didType: polotnoTypes.Avatar,
                        borderEnabled: false,
                        originalWidth,
                        originalHeight,
                        ...(inheritedProperties?.custom || {}),
                    },
                    ...(options?.skipCrop
                        ? {
                              x: presenter.x,
                              y: presenter.y,
                              width: presenter.width,
                              height: presenter.height,
                          }
                        : inheritedProperties
                          ? {
                                x: inheritedProperties.x,
                                y: inheritedProperties.y,
                                width: inheritedProperties.width,
                                height: inheritedProperties.height,
                            }
                          : {
                                x: shouldResize ? 0 : x,
                                y: shouldResize ? 0 : y,
                                width,
                                height,
                            }),
                };

                page.addElement(element);
            });
        } else {
            setBufferedAvatar({ presenter, options });
        }
    };

    return addAvatar;
};
